import type { CompanyOption, HouseTreeNode } from '../types/catalog'
import type { InternalCompany } from '../types/internalCompany'

export function toCompanyOptions(internal: InternalCompany[]): CompanyOption[] {
  return internal.map((company) => ({
    id: company.id,
    name: company.name,
    businesses: company.businesses.map((business) => ({
      id: business.id,
      name: business.name ?? business.type,
    })),
  }))
}

function houseCountFor(business: { farms: { houses: unknown[] }[] }): number {
  return business.farms.reduce((sum, farm) => sum + farm.houses.length, 0)
}

export function toHouseTree(
  internal: InternalCompany[],
  companyId: string,
): HouseTreeNode[] {
  if (!companyId) return []
  const company = internal.find((entry) => entry.id === companyId)
  if (!company) return []

  const companyHouseTotal = company.businesses.reduce(
    (sum, business) => sum + houseCountFor(business),
    0,
  )

  return [
    {
      label: company.name,
      count: companyHouseTotal,
      children: company.businesses.map((business) => ({
        label: business.name ?? business.type,
        count: houseCountFor(business),
        children: business.farms.map((farm) => ({
          label: farm.name,
          count: farm.houses.length,
          children: farm.houses.map((house) => ({
            label: house.name,
            leafValue: house.id,
          })),
        })),
      })),
    },
  ]
}

export function flattenHouseLeaves(tree: HouseTreeNode[]): string[] {
  const out: string[] = []
  const walk = (nodes: HouseTreeNode[]): void => {
    for (const node of nodes) {
      if (node.leafValue) out.push(node.leafValue)
      if (node.children) walk(node.children)
    }
  }
  walk(tree)
  return out
}

export function houseLeafLabelMap(tree: HouseTreeNode[]): Record<string, string> {
  const map: Record<string, string> = {}
  const walk = (nodes: HouseTreeNode[]): void => {
    for (const node of nodes) {
      if (node.leafValue) map[node.leafValue] = node.label
      if (node.children) walk(node.children)
    }
  }
  walk(tree)
  return map
}
